import type { OrbExtension } from "lib/orb-qr";
import { Layout } from "components/Layout";
import { memo, useContext, useEffect, useState } from "react";
import { CollectionDetailsContext } from "contexts/CollectionDetails";
import { useLocation } from "react-router-dom";
import { AppRoutes } from "types";
import { Button } from "components/Form/Button";
import { getUserEmail } from "lib/get-user-email";
import { getCollectionDetails } from "lib/get-collection-details";
import { captureDB } from "lib/indexed-db";
import {
  getPrismicTaskInstructions,
  extractOrbExtensionFromInstructions,
} from "lib/prismic";
import { useOrbQrContent } from "hooks/useOrbQrContent";
import { OrbQRCode } from "components/OrbQRCode";

export const Orb = memo(function Orb() {
  const [collectionDetailsData, setCollectionDetailsData] = useState<{
    collection_details?: Array<string>;
    exif?: object;
  } | null>(null);
  const [storedCollectionDetails, setRawCollectionDetails] =
    useState<any>(null);
  const [collectionDetailsLoaded, setCollectionDetailsLoaded] = useState(false);
  const [orbExtension, setOrbExtension] = useState<OrbExtension>(0);
  const location = useLocation();
  const { config } = useContext(CollectionDetailsContext);
  const selectedCollection = config?.selectedCollection;

  const taskId = location.state?.taskId;
  const taskName = location.state?.taskName;

  // Load collection details from storage
  useEffect(() => {
    let cancelled = false;
    setCollectionDetailsLoaded(false);
    setCollectionDetailsData(null);
    setRawCollectionDetails(null);
    setOrbExtension(0);

    async function loadCollectionDetails() {
      try {
        const details = await getCollectionDetails(
          taskId,
          undefined,
          selectedCollection
        );
        if (cancelled) return;
        setCollectionDetailsData(details || null);

        // Load raw collection details from IndexedDB for QR generation
        if (taskId) {
          const storedCaptures = await captureDB.getStepCaptures(taskId);
          if (cancelled) return;
          const step0 = storedCaptures.find((s) => s.stepNumber === 0);
          if (step0?.collectionDetails) {
            setRawCollectionDetails(step0.collectionDetails);
          }

          const instructions = await getPrismicTaskInstructions(
            taskId,
            selectedCollection
          );
          if (cancelled) return;
          const ext = extractOrbExtensionFromInstructions(instructions);
          if (ext !== null) {
            setOrbExtension(ext);
          }
        }
      } catch (err) {
        console.error("Error loading collection details:", err);
        if (!cancelled) setCollectionDetailsData(null);
      } finally {
        if (!cancelled) setCollectionDetailsLoaded(true);
      }
    }

    loadCollectionDetails();
    return () => {
      cancelled = true;
    };
  }, [taskId, selectedCollection]);

  const { content: qrContent } = useOrbQrContent({
    captureTaskId: taskId,
    extension: orbExtension,
    storedDetails: storedCollectionDetails,
    includeDetails: Boolean(
      collectionDetailsData?.collection_details && storedCollectionDetails
    ),
    detailsLoaded: collectionDetailsLoaded,
  });

  return (
    <Layout
      toolbar={<Button to={AppRoutes.home}>Go Back</Button>}
      toolbarClassName="bg-white !py-3"
    >
      <div className="relative h-full grid grid-rows-[auto_1fr] items-center gap-12 text-center">
        <div className="space-y-6">
          <div className="grid gap-2">
            <h1 className="text-24 font-semibold">
              {taskName || "Device capture"}
            </h1>
            <div className="text-858494">
              <p>Please show this QR code to the Orb device.</p>
            </div>
          </div>

          <div className="grid gap-1 justify-center font-medium">
            <p>Signed in as</p>
            <span className="bg-858494/10 rounded-[20px] px-3 py-2">
              {getUserEmail()}
            </span>
          </div>
        </div>

        {qrContent ? (
          <OrbQRCode content={qrContent} />
        ) : (
          <div className="flex items-center justify-center">
            <div className="flex flex-col items-center gap-4">
              <svg
                className="animate-spin h-8 w-8 text-3461fd"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                aria-hidden
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                />
              </svg>
              <div className="text-858494">Generating QR code...</div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
});
